<template>
  <div ref="segmentFilterRef">
    <teleport to="#app" v-if="isOpen">
      <div class="segment-filter-overlay" @click.prevent="onClickClose" />
    </teleport>
    <div class="segment-filter-wrp">
      <template v-if="segmentStyle === 'button'">
        <label :class="['segment-filter-button', selectedValue.length && 'has-select', isOpen && 'is-open']" @click="onClickOpen">
          <!-- Adjust the icon here -->
          <SegmentIcon class="icon-class" />
          {{ false ? placeholder : 'กรุณาเลือกกลุ่มเป้าหมาย' }}
        </label>
        <div class="reset-button-wrp ml-2" v-bt-tooltips :title="t('bi.reset')" @click="onClickReset"><reset-icon /></div>
      </template>

      <teleport to="#app" v-if="isOpen">
        <div v-if="isOpen" class="segment-dropdown-wrp" :style="{ top: `${position.top}px`, left: `${position.left}px` }">
          <treeselect
            isDefaultExpanded
            v-model="selectedValue"
            :default-expand-level="2"
            :multiple="true"
            :options="optionList?.options"
            :alwaysOpen="true"
          />
        </div>
      </teleport>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
// import the component
import Treeselect from 'vue3-treeselect';
// import the styles
import 'vue3-treeselect/dist/vue3-treeselect.css';

//libs
import { useI18n } from 'vue-i18n';

// Icon
import ResetIcon from '@/assets/icons/modules/analytics/bi/ResetIcon.vue';
import SegmentIcon from '@/assets/icons/base/churn-rate/SegmentIcon.vue';

import { useElementBounding } from '@vueuse/core';

interface Props {
  modelValue: string[];
  optionList: Bi.Filter.DynamicOptions | null;
  placeholder: string;
  segmentStyle?: 'button' | 'dropdown';
}

interface Position {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

const { t } = useI18n();

const props = withDefaults(defineProps<Props>(), { placeholder: 'Dynamic Filter', segmentStyle: 'button' });
const emit = defineEmits(['update:modelValue', 'hide', 'reset']);

const segmentFilterRef = ref(null);

const selectedValue = ref<string[]>([]);
const isOpen = ref<boolean>(false);

const position = ref<Position>({ top: 0, left: 0, right: 0, bottom: 0 });

const getPosition = () => {
  if (segmentFilterRef.value) {
    const elRect = useElementBounding(segmentFilterRef.value);

    if (elRect) {
      // 30 is button height
      // 40 is dropdown height
      position.value.top = elRect.top.value + (props.segmentStyle == 'button' ? 30 : 42);
      position.value.left = elRect.left.value;
    }
  }
};

const onClickOpen = () => {
  if (isOpen.value) {
    onClickClose();
    return (isOpen.value = false);
  }

  getPosition();
  isOpen.value = true;
};

const onClickClose = () => {
  emit('hide');
  isOpen.value = false;
};

const onClickReset = () => {
  if (selectedValue.value.length) {
    selectedValue.value = [];
    setTimeout(() => emit('reset'), 10);

    if (isOpen.value) {
      isOpen.value = false;
    }
  }
};

watch(
  () => selectedValue.value,
  () => {
    emit('update:modelValue', selectedValue.value);
  },
  { deep: true },
);

watch(
  () => props.modelValue,
  () => {
    if (props.modelValue) {
      selectedValue.value = props.modelValue;
    }
  },
  { deep: true },
);

onMounted(() => {
  if (props.modelValue) {
    selectedValue.value = props.modelValue;
  }
});
</script>

<style scoped lang="scss">
.segment-filter-overlay {
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  position: fixed;
  overflow: hidden;
  z-index: 100;
  cursor: default;
}

.segment-filter-wrp {
  position: relative;
  display: flex;
  align-items: center;
  font-size: 16px;

  .segment-filter-button {
    font-size: 16px;
    cursor: pointer;
    padding: 2px 6px;
    border-radius: 6px;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: 0.2s;
    color: #606a6c;

    &:hover,
    &.is-open {
      background-color: #e5f2ff;
    }

    &.has-select {
      background-color: #012f64;
      color: #ffffffff;

      .icon-class {
        color: #ffffffff;
      }
    }
  }

  .segment-filter-dropdown {
    background-color: #ffffffff;
    border-radius: 10px;
    border: 1px solid #ededed;
    padding: 4px;
    white-space: nowrap;
    height: 40px;
    display: flex;
    align-items: center;

    &:hover {
      box-shadow: 0 0 1rem lightgrey;
    }
    .arrow-button {
      margin-left: 0.5rem;
      &.opened svg {
        transform: rotate(180deg);
      }

      svg {
        transition: 0.2s all ease-in-out;
      }
    }
  }

  .reset-button-wrp {
    width: 22px;
    height: 22px;
    border-radius: 100%;
    background-color: rgb(96, 106, 108, 0.15);
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 4.5px;
    color: #ffffffff;
    cursor: pointer;

    svg {
      color: #606A6C;
      transition: 0.2s all;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    &:hover {
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
      background-color: rgb(96, 106, 108, 0.3);

      svg {
        transform: rotate(45deg);
      }
    }
  }
}
.segment-dropdown-wrp {
  position: fixed;
  top: 50%;
  left: 50%;
  border-radius: 0.5rem;
  width: 300px;
  z-index: 9999;
}

.icon-class {
  width: 16px;
  height: 16px;
  vertical-align: middle;
}
</style>
